import styled, { css } from 'styled-components';

import { fontWeights } from 'styles/variables';
import { caption } from 'styles/typography';
import { container, columnsGutter } from 'styles/grid';
import { minWidth } from 'styles/media-queries';
import { Paragraph } from 'components/atoms';
import { getModifierStyles } from 'utils/get-modifier-styles';
import { PAGES } from 'constants/content-types';
import { gray50, solidBlack0 } from '@spotify-internal/encore-web';
import { black, gray, white } from 'styles/colors';

// Modifier styles
const ROOT_USE_GRID = css`
  ${container}
  ${columnsGutter}

  display: grid;
  grid-template-columns: repeat(12, 1fr);
  margin: 3.2rem auto;

  ${minWidth.lg`
    margin: 6.4rem auto;
`}
`;

const ROOT_NO_MARGIN = css`
  margin: 0;
`;

const CONTAINER_USE_GRID = css`
  grid-column: 1 / 13;

  ${minWidth.lg`
    grid-column: 4 / 10;
  `}
`;

export const MODIFIERS = {
  useGrid: 'useGrid',
  contentTile: 'contentTile',
};

// Style modifier map
const STYLE_MAP = {
  Root: {
    [MODIFIERS.useGrid]: ROOT_USE_GRID,
    [PAGES.HOME]: ROOT_USE_GRID,
    [PAGES.DETAIL]: ROOT_USE_GRID,
    [PAGES.LANDING]: ROOT_USE_GRID,
    [MODIFIERS.contentTile]: ROOT_NO_MARGIN,
  },
  Container: {
    [MODIFIERS.useGrid]: CONTAINER_USE_GRID,
    [PAGES.HOME]: CONTAINER_USE_GRID,
    [PAGES.DETAIL]: CONTAINER_USE_GRID,
    [PAGES.LANDING]: CONTAINER_USE_GRID,
  },
  Player: {
    [MODIFIERS.contentTile]: css`
      background-color: transparent !important;
      height: 4rem !important;
    `,
  },
  PlayerControls: {
    [MODIFIERS.contentTile]: css`
      padding: 0 !important;
      svg {
        width: 3.6rem;
        height: 3.6rem;
      }
    `,
  },
  Title: {
    [MODIFIERS.contentTile]: css`
      margin-bottom: 1.6rem !important;
    `,
  },
  Caption: {
    [MODIFIERS.contentTile]: css`
      margin-top: 1.6rem !important;
    `,
  },
};

export const Root = styled.div`
  margin: 6.4rem auto;

  ${({ theme }) => theme.modifier && getModifierStyles(theme, STYLE_MAP.Root)}
`;

export const Container = styled.div`
  position: relative;

  ${({ theme }) =>
    theme.modifier && getModifierStyles(theme, STYLE_MAP.Container)}
`;

export const Title = styled(Paragraph)`
  color: ${props => props.titleColor || solidBlack0};
  font-weight: ${fontWeights.black};
  letter-spacing: -0.025rem;
  margin-bottom: 2.2rem;

  ${minWidth.lg`
    font-size: 2rem;
    letter-spacing: -0.05rem;
    margin-bottom: 3rem;
  `}

  ${({ theme }) => theme.modifier && getModifierStyles(theme, STYLE_MAP.Title)}
`;

export const Caption = styled.p`
  ${caption}

  color: ${({ theme }) => (theme.isDarkTheme ? black : gray50)};
  font-weight: ${fontWeights.normal};
  margin-top: 2.4rem;

  ${minWidth.lg`
    margin-top: 3.2rem;
  `}

  ${({ theme }) =>
    theme.modifier && getModifierStyles(theme, STYLE_MAP.Caption)}
`;

export const Player = styled.div`
  align-items: center;
  background-color: ${black};
  border-radius: 0.6rem;
  display: flex;
  height: 5.4rem;

  ${minWidth.lg`
    height: 9rem;
    border-radius: 0.8rem;
  `}

  ${({ theme }) => theme.modifier && getModifierStyles(theme, STYLE_MAP.Player)}
`;

export const Audio = styled.audio`
  display: none;
`;

export const PlayerControls = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex: 1;
  padding-left: 1.6rem;
  padding-right: 1.6rem;

  ${minWidth.lg`
    padding-left: 2rem;
    padding-right: 2rem;
  `}

  ${({ theme }) =>
    theme.modifier && getModifierStyles(theme, STYLE_MAP.PlayerControls)}
`;

export const PlayButton = styled.button`
  cursor: pointer;
  height: 3.2rem;
  width: 3.2rem;

  svg {
    circle {
      fill: ${({ theme }) => (theme.isDarkTheme ? black : white)};
    }
    path {
      fill: ${({ theme }) => (theme.isDarkTheme ? white : black)};
    }
  }

  &:hover {
    transform: scale(0.95);
    transition: smooth;
  }

  ${minWidth.lg`
    height: 4.8rem;
    width: 4.8rem;
  `}
`;

export const Loading = styled.div`
  height: 100%;
  width: 100%;

  & path {
    animation: rotate 1000ms linear infinite;
    transform-origin: 50% 50%;
  }
`;

export const ProgressContainer = styled.div`
  background-color: ${gray};
  border-radius: 0.2rem;
  display: flex;
  flex: 1;
  height: 0.3rem;
  margin: 0 1.6rem;
  overflow: hidden;
  position: relative;

  ${minWidth.lg`
    margin: 0 2.4rem;
  `}
`;

export const ProgressBar = styled.div`
  background-color: ${({ theme }) => (theme.isDarkTheme ? black : white)};
  border-radius: 0.2rem;
  height: 100%;
  left: -100%;
  position: absolute;
  transform: translateX(0%);
  width: 100%;
`;

export const Timer = styled(Paragraph).attrs({
  styling: 'p',
})`
  color: ${({ theme }) => (theme.isDarkTheme ? black : white)};
  font-size: 1.4rem;
  font-weight: ${fontWeights.normal};
  letter-spacing: normal;
  text-align: center;
  white-space: nowrap;
  width: 9.2rem;

  ${minWidth.lg`
    font-size: 1.6rem;
    letter-spacing: normal;
    width: 10.5rem;
  `}
`;
